import React, { useEffect, useState } from "react";

import { Select, SelectProps } from "antd";

import { AssignableUserOption, fetchAssignableUsers, getCachedAssignableUsers, searchUsers } from "../store/userSlice";
import DebounceSelect from "./DebounceSelect";

export type AssigneeSelectProps = Omit<SelectProps<any>, "options" | "children"> & {
  /** Callers that manage the selection themselves pass this (null = nothing selected). */
  selected?: any;
  /**
   * Offer users an admin has switched off too (FOC-5953). For filtering a list by who already
   * holds documents, never for choosing who to hand work to.
   */
  includeDisabled?: boolean;
};

/**
 * The Assigned To picker (FOC-5820).
 *
 * Where the API can name every assignable user — the people who have signed in to this OCR
 * instance — this is a real dropdown: the list is there as soon as it is opened, and typing
 * filters it. Where it cannot (instances whose picker is not restricted to app users, so the
 * assignable set is the whole directory) it falls back to searching as the user types.
 */
function AssigneeSelect({ selected, includeDisabled, ...props }: AssigneeSelectProps) {
  // Start from the cached list where there is one, so a remount does not flash the search-only
  // fallback before the (already known) options arrive.
  const [{ options, loading }, setState] = useState<{ options: AssignableUserOption[] | null; loading: boolean }>(() => {
    const cached = getCachedAssignableUsers({ includeDisabled });
    return { options: cached?.isComplete ? cached.users : null, loading: cached === null };
  });

  useEffect(() => {
    let cancelled = false;
    fetchAssignableUsers({ includeDisabled }).then((result) => {
      if (!cancelled) {
        setState({ options: result.isComplete ? result.users : null, loading: false });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [includeDisabled]);

  if (!options) {
    // Still loading, or this instance has no bounded list to show.
    // `loading` only while the list is still on its way: passing it once resolved would override
    // DebounceSelect's own spinner for searches.
    return <DebounceSelect selected={selected} fetchOptions={searchUsers} {...(loading ? { loading: true } : {})} {...props} />;
  }

  return (
    <Select
      showSearch
      allowClear
      labelInValue
      size="large"
      optionFilterProp="label"
      filterOption={(input, option) => `${option?.label ?? ""}`.toLowerCase().includes(input.toLowerCase())}
      {...(selected !== undefined ? { value: selected } : {})}
      {...props}
      options={options}
    />
  );
}

export default AssigneeSelect;
